/**
 * Data plane — Semantica catalog (L2, ADR-0026). The standalone instance-wide
 * surface that finally earns the semantic layer a sidebar home: one row per
 * dataset with its L2 coverage (annotated/total fields), PII/sensitivity flag,
 * domain and last-update. Grouped by owner (app/source), searchable; each row
 * links to the per-dataset semantics editor.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Loader2, Network, Search, ShieldAlert, Sparkles } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { SemanticsDetailDrawer } from "@/components/datasets/SemanticsDetailDrawer";
import { Badge } from "@/components/ui/badge";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { cn } from "@/lib/cn";
import { datasetSemanticsApi, type SemanticsOverviewRow } from "@/lib/api-dataset-semantics";

const SENSITIVITY_TONE: Record<string, "neutral" | "warning" | "danger"> = {
  public: "neutral",
  internal: "neutral",
  confidential: "warning",
  restricted: "danger",
};

export function DatasetSemanticsCatalogPage() {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");
  const [openRow, setOpenRow] = React.useState<SemanticsOverviewRow | null>(null);
  const q = useQuery({
    queryKey: ["datasets", "semantics", "overview"],
    queryFn: () => datasetSemanticsApi.overview(),
  });

  const rows = React.useMemo(() => q.data ?? [], [q.data]);
  const groups = React.useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = rows.filter(
      (r) =>
        !needle ||
        r.name.toLowerCase().includes(needle) ||
        r.owner.toLowerCase().includes(needle) ||
        (r.domain ?? "").toLowerCase().includes(needle),
    );
    const byOwner = new Map<string, SemanticsOverviewRow[]>();
    for (const r of filtered) (byOwner.get(r.owner) ?? byOwner.set(r.owner, []).get(r.owner)!).push(r);
    return [...byOwner.entries()]
      .map(([owner, items]) => ({ owner, items: items.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.owner.localeCompare(b.owner));
  }, [rows, search]);

  const annotated = rows.filter((r) => r.has_semantics).length;
  const withPii = rows.filter((r) => r.has_pii).length;

  return (
    <PageContainer>
      <PageHeader
        title={t("semanticsCatalog.title", "Semantica")}
        description={t(
          "semanticsCatalog.subtitle",
          "Il significato dei dataset dell'istanza: copertura semantica, dati sensibili (PII) e dominio. Apri un dataset per descriverne i campi.",
        )}
        actions={
          rows.length > 0 ? (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>
                <span className="font-semibold text-foreground">{annotated}</span>/{rows.length}{" "}
                {t("semanticsCatalog.described", "descritti")}
              </span>
              {withPii > 0 && (
                <span className="flex items-center gap-1 text-warning">
                  <ShieldAlert className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {withPii} {t("semanticsCatalog.withPii", "con PII")}
                </span>
              )}
            </div>
          ) : undefined
        }
      />
      <PageContent>
        <div className="mb-3 flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 sm:max-w-sm">
          <Search className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("semanticsCatalog.search", "Cerca dataset, sorgente o dominio…")}
            className="h-8 w-full bg-transparent text-sm focus:outline-none"
          />
        </div>

        {q.isLoading ? (
          <div className="flex items-center gap-2 py-10 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            {t("common.loading", "Caricamento…")}
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            icon={Network}
            title={t("semanticsCatalog.empty", "Nessun dataset")}
            description={t(
              "semanticsCatalog.emptyDescription",
              "Registra dei dataset per descriverne la semantica.",
            )}
          />
        ) : (
          <div className="space-y-5">
            {groups.map((grp) => (
              <div key={grp.owner}>
                <div className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {grp.owner}
                </div>
                <div className="overflow-hidden rounded-lg border border-border/60">
                  {grp.items.map((r, i) => (
                    <button
                      key={r.dataset_id}
                      type="button"
                      onClick={() => setOpenRow(r)}
                      className={cn(
                        "group flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-surface",
                        i > 0 && "border-t border-border/60",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground">{r.name}</span>
                        {r.domain && (
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {r.domain}
                          </span>
                        )}
                      </span>

                      {/* Coverage */}
                      <span className="hidden w-28 shrink-0 sm:block">
                        <Coverage annotated={r.annotated_count} total={r.field_count} />
                      </span>

                      {/* PII + sensitivity */}
                      <span className="flex w-32 shrink-0 items-center justify-end gap-1.5">
                        {r.has_pii && (
                          <Badge tone="warning" className="text-[10px]">
                            <ShieldAlert className="mr-0.5 h-3 w-3" strokeWidth={1.5} />
                            PII
                          </Badge>
                        )}
                        {r.sensitivity && r.sensitivity !== "internal" && (
                          <Badge tone={SENSITIVITY_TONE[r.sensitivity] ?? "neutral"} className="text-[10px]">
                            {r.sensitivity}
                          </Badge>
                        )}
                      </span>

                      {/* Status / CTA */}
                      <span className="flex w-24 shrink-0 items-center justify-end gap-1 text-xs text-muted-foreground">
                        {r.has_semantics ? (
                          <Sparkles className="h-3.5 w-3.5 text-accent" strokeWidth={1.5} />
                        ) : (
                          <span className="italic">{t("semanticsCatalog.todo", "da descrivere")}</span>
                        )}
                        <ChevronRight
                          className="h-3.5 w-3.5 text-muted-foreground transition group-hover:text-foreground"
                          strokeWidth={1.5}
                        />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </PageContent>

      <SemanticsDetailDrawer row={openRow} onClose={() => setOpenRow(null)} />
    </PageContainer>
  );
}

/** Compact coverage bar: annotated / total live fields. */
function Coverage({ annotated, total }: { annotated: number; total: number | null }) {
  const { t } = useTranslation();
  if (total == null) {
    return (
      <span className="text-[11px] text-muted-foreground">
        {annotated} {t("semanticsCatalog.fields", "campi")}
      </span>
    );
  }
  const pct = total === 0 ? 0 : Math.round((annotated / total) * 100);
  return (
    <span className="block">
      <span className="mb-0.5 block text-[10px] text-muted-foreground">
        {annotated}/{total} {t("semanticsCatalog.fields", "campi")}
      </span>
      <span className="block h-1.5 overflow-hidden rounded-full bg-border">
        <span
          className={cn("block h-full rounded-full", pct >= 100 ? "bg-success" : "bg-accent")}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
}
